import {BlockWithAlignableContents} from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';

type KalturaComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  videoID: string;
}>;

const KalturaComponent = ({className, format, nodeKey}: KalturaComponentProps) => {
  return (
    <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
      <iframe
        width="100%"
        height="390"
        src={`https://dropbox.mediaspace.kaltura.com/embed/secure/iframe/entryId/1_gqe8hzv9/uiConfId/35727412`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen={true}
        title="Kaltura video"
      />
    </BlockWithAlignableContents>
  );
};

export type SerializedKalturaNode = Spread<
  {
    videoID: string;
  },
  SerializedDecoratorBlockNode
>;

function convertKalturaElement(domNode: HTMLElement): null | DOMConversionOutput {
  const videoID = domNode.getAttribute('data-lexical-kaltura');
  if (videoID) {
    const node = $createKalturaNode(videoID);
    return {node};
  }
  return null;
}

export class KalturaNode extends DecoratorBlockNode {
  __id: string;

  static getType(): string {
    return 'kaltura';
  }

  static clone(node: KalturaNode): KalturaNode {
    return new KalturaNode(node.__id, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedKalturaNode): KalturaNode {
    const node = $createKalturaNode(serializedNode.videoID);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedKalturaNode {
    return {
      ...super.exportJSON(),
      type: 'kaltura',
      version: 1,
      videoID: this.__id,
    };
  }

  constructor(id: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__id = id;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('iframe');
    element.setAttribute('data-lexical-kaltura', this.__id);
    element.setAttribute('width', '100%');
    element.setAttribute('height', '390');
    element.setAttribute('src', `https://dropbox.mediaspace.kaltura.com/media/${this.__id}`);
    element.setAttribute('frameborder', '0');
    element.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    element.setAttribute('allowfullscreen', 'true');
    element.setAttribute('title', 'Kaltura video');
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      iframe: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-kaltura')) {
          return null;
        }
        return {
          conversion: convertKalturaElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  getId(): string {
    return this.__id;
  }

  getTextContent(): string {
    return `https://dropbox.mediaspace.kaltura.com/media/${this.__id}`;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig) {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <KalturaComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        videoID={this.__id}
      />
    );
  }
}

export function $createKalturaNode(videoID: string): KalturaNode {
  return new KalturaNode(videoID);
}

export function $isKalturaNode(
  node: KalturaNode | LexicalNode | null | undefined
): node is KalturaNode {
  return node instanceof KalturaNode;
}
